import { morphSection, sectionRenderer } from '@theme/section-renderer';
import { buildProductDataPayload } from '@theme/mb-purchase-options';

class MbSubscriptionToggle extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.input = this.querySelector('input[type="checkbox"]');

    if (!this.input) return;

    this.cartItemComponent = this.closest('cart-items-component');

    if(this.cartItemComponent) {
      this.cartItemSectionId = this.cartItemComponent.dataset.sectionId;
    }

    this.handleChange = this.handleChange.bind(this);
    this.input.addEventListener('change', this.handleChange);
  }

  disconnectedCallback() {
    if (this.input) {
      this.input.removeEventListener('change', this.handleChange);
    }
  }

  get cartLineKey() {
    return this.input.dataset.cartLineKey;
  }

  get cartItemQuantity() {
    return Number(
      this.input.dataset.itemQuantity
    );
  }

  get defaultSubscriptionID() {
    return Number(
      this.input.dataset.defaultSellingPlan
    );
  }

  get lineItemProperties() {
    const propertiesArray = JSON.parse(this.input.dataset.lineItemProperties);
    return propertiesArray ? Object.fromEntries(propertiesArray) : {};
  }

  enableInput() {
    if (this.input) {
      this.input.disabled = false;
    }
  }

  disableInput() {
    if (this.input) {
      this.input.disabled = true;
    }
  }

  handleChange(event) {
    const isChecked = event.target.checked;

    /* 
      Dispatch a custom event so other scripts can react
      currently not dispatching to further understand dispatch event logic connecting to cart drawer updates! 
    */
    // this.dispatchEvent(
    //   new CustomEvent('mb-toggle:change', {
    //     detail: { checked: isChecked },
    //     bubbles: true
    //   })
    // );

    // Block enabling if no default subscription ID
    if (isChecked && !this.defaultSubscriptionID) {
      event.target.checked = false;
      console.warn("Upgrade to subscription: No selling plan available to upgrade to subscription")
      return;
    }

    // Skip updation if no cartlineKey is found
    if(!this.cartLineKey) {
      event.target.checked = !isChecked;
      console.warn("Upgrade to subscription: No cart line key found for update")
      return;
    }

    if (isChecked) {
      this.enableSubscription();
    } else {
      this.disableSubscription();
    }
  }

  // While removing a subscription, the corresponding line item properties need to be removed from existing
  removeSubscriptionKeys(properties) {
    const keysToRemove = [
      "_selling_plan",
      "_line_item_id",
      "_subscription_terms",
      "_preupsell_product_selling_plan",
      "_pricebook"
    ];

    const updated = { ...properties };

    keysToRemove.forEach(key => {
      delete updated[key];
    });

    return updated;
  }

  enableSubscription() {
    const container = this.closest('#cart-line-subscription-toggle');
    if (!container) {
      console.warn("Upgrade to subscription: no subscription details found");
      this.input.checked = false;
      return;
    }

    const productData = buildProductDataPayload(container);
    
    if(!productData) {
      console.warn("Upgrade to subscription: no product data found from subscription details container");
      this.input.checked = false;
      return;
    }

    // Filter out empty or null properties before calling the API
    const itemProperties = Object.keys(productData.properties || {}).reduce((acc, key) => {
      const value = productData.properties[key];

      if (value !== "" && value != null) {
        acc[key] = value;
      }

      return acc;
    }, {});

    const payload = {
      id: this.cartLineKey,
      quantity: this.cartItemQuantity,
      selling_plan: this.defaultSubscriptionID,
      properties: itemProperties
    }

    this.updateCartLine(payload);
  }

  disableSubscription() {
    const payload = {
      id: this.cartLineKey,
      quantity: this.cartItemQuantity,
      selling_plan: null,
      properties : {
        ...this.removeSubscriptionKeys(this.lineItemProperties)
      },
    }

    this.updateCartLine(payload);
  }

  async updateCartLine(payload) {
    if (!this.input) return;

    const previousState = this.input.checked;

    try {
      // Prevent rapid toggles while request is running
      this.disableInput();
      this.cartItemComponent.classList.add('cart-items-disabled');

      const response = await fetch('/cart/change.js', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({
          ...payload,
          sections: this.cartItemSectionId
        })
      });

      if (!response.ok) {
        const cartError = await response.json();
        const message = cartError?.description || cartError?.message || '';
        this.cartItemComponent.handleSubscriptionToggleError(this.cartLineKey, message);
        throw new Error(`Cart API failed: ${response.status}`);
      }

      const cart = await response.json();

      if (!cart || cart.errors) {
        throw new Error(cart.errors || 'Cart validation failed');
      }

      if (cart.sections && cart.sections[this.cartItemSectionId]) {
        morphSection(
          this.cartItemSectionId,
          cart.sections[this.cartItemSectionId]
        );
      }

    } catch (error) {
      console.error('Upgrade to subscription: Error updating cart line', error);

      // Revert toggle back to previous state
      this.input.checked = !previousState;
      
      // show user feedback: handle error state in UI
    } finally {
      // this.input.disabled = false;
      this.cartItemComponent.classList.remove('cart-items-disabled');
    }
  }

}

customElements.define('mb-subscription-toggle', MbSubscriptionToggle);